import { h, render, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ConfirmStartCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmStartCampaignDialog.vue';

// Traduz a falha do disparo num motivo ACIONÁVEL pro operador, em vez do genérico
// "não foi possível" — que escondia 401 (sessão expirada após logout/login), 404
// (id da lista desatualizado) e queda de rede, deixando o operador sem saber o que fazer.
function dripErrorMessage(error, fallback) {
  const serverMsg = error?.response?.data?.error;
  if (serverMsg) return serverMsg;
  const status = error?.response?.status;
  if (status === 401) {
    return 'Sua sessão expirou. Recarregue a página (F5), entre de novo e tente outra vez.';
  }
  if (status === 404) {
    return 'Campanha não encontrada. Recarregue a página (F5) e tente novamente.';
  }
  if (!error?.response) {
    return 'Falha de conexão com o servidor. Verifique a internet e tente novamente.';
  }
  return fallback;
}

// Controles do disparo em massa (drip) reaproveitados pelas páginas de campanha
// (WhatsApp/WAHA e SMS) — evita duplicar/divergir os handlers entre elas.
export function useCampaignDrip() {
  const store = useStore();
  const { t } = useI18n();
  // appContext do app principal: o diálogo de confirmação é montado por fora da
  // árvore das páginas (que não têm slot pra ele), mas precisa herdar i18n/store.
  const appContext = getCurrentInstance()?.appContext;

  // Confirmação nativa (Dialog do DS) no lugar do window.confirm — monta o
  // ConfirmStartCampaignDialog em um host descartável e resolve true/false.
  // confirmOpen: o confirm antigo bloqueava a thread; o diálogo é assíncrono,
  // então duplo-clique no Iniciar abriria 2 diálogos sobrepostos (e 2 starts).
  let confirmOpen = false;
  const confirmStart = (campaign, preview) =>
    new Promise(resolve => {
      if (confirmOpen) {
        resolve(false);
        return;
      }
      confirmOpen = true;
      const host = document.createElement('div');
      document.body.appendChild(host);
      const cleanup = value => {
        confirmOpen = false;
        render(null, host);
        host.remove();
        resolve(value);
      };
      const vnode = h(ConfirmStartCampaignDialog, {
        campaign,
        preview,
        onConfirm: () => cleanup(true),
        onCancel: () => cleanup(false),
      });
      if (appContext) vnode.appContext = appContext;
      render(vnode, host);
    });

  const handleStart = async campaign => {
    // Guard-rail: disparo em massa é irreversível (manda mensagem real a muita
    // gente). Mostra a prévia e exige confirmação antes de iniciar. Se a prévia
    // falhar (sessão/rede), NÃO dispara às cegas: mostra o motivo real e aborta —
    // antes o erro era engolido e o start saía sem confirmação nem prévia.
    let preview;
    try {
      preview = await store.dispatch('campaigns/previewDrip', campaign.id);
    } catch (error) {
      useAlert(
        dripErrorMessage(
          error,
          'Não foi possível carregar a prévia do disparo.'
        )
      );
      return;
    }

    const total = Number(preview?.total_pending) || 0;
    if (total === 0) {
      useAlert(
        'Nenhum contato válido na audiência — verifique o público. O disparo não foi iniciado.'
      );
      return;
    }

    const confirmed = await confirmStart(campaign, preview);
    if (!confirmed) return;

    try {
      await store.dispatch('campaigns/startDrip', campaign.id);
      useAlert(
        'Disparo iniciado — o envio vai acontecendo gradualmente por dia.'
      );
    } catch (error) {
      useAlert(dripErrorMessage(error, 'Não foi possível iniciar o disparo.'));
    }
  };

  const handlePause = async campaign => {
    try {
      await store.dispatch('campaigns/pauseDrip', campaign.id);
      useAlert('Disparo pausado.');
    } catch (error) {
      useAlert(dripErrorMessage(error, 'Não foi possível pausar.'));
    }
  };

  const handleResume = async campaign => {
    try {
      await store.dispatch('campaigns/resumeDrip', campaign.id);
      useAlert('Disparo retomado.');
    } catch (error) {
      useAlert(dripErrorMessage(error, 'Não foi possível retomar.'));
    }
  };

  const handleCancel = async campaign => {
    try {
      await store.dispatch('campaigns/cancelDrip', campaign.id);
      useAlert(t('CAMPAIGN.DRIP.ALERTS.CANCEL_SUCCESS'));
    } catch (error) {
      useAlert(dripErrorMessage(error, t('CAMPAIGN.DRIP.ALERTS.CANCEL_ERROR')));
    }
  };

  const handlePreview = async campaign => {
    try {
      const started = ['running', 'paused', 'completed'].includes(
        campaign.drip_status
      );
      if (started) {
        const data = await store.dispatch(
          'campaigns/fetchRecipients',
          campaign.id
        );
        // resposta estruturada: { status: {...}, variants: {...} }
        const statusCounts = data?.status || {};
        const variantCounts = data?.variants || {};
        const parts = Object.entries(statusCounts).map(
          ([status, n]) => `${status}: ${n}`
        );
        const variantParts = Object.entries(variantCounts).map(
          ([v, n]) => `${v}: ${n}`
        );
        const variantSuffix = variantParts.length
          ? ` | A/B — ${variantParts.join(', ')}`
          : '';
        useAlert(
          parts.length
            ? `Destinatários — ${parts.join(', ')}${variantSuffix}`
            : 'Sem destinatários ainda.'
        );
      } else {
        const p = await store.dispatch('campaigns/previewDrip', campaign.id);
        useAlert(
          `Prévia: ${p.total_pending} contatos válidos, ${p.opted_out} opt-out, ~${p.estimated_days} dia(s) de envio.`
        );
      }
    } catch (error) {
      useAlert('Não foi possível carregar a prévia.');
    }
  };

  return {
    handleStart,
    handlePause,
    handleResume,
    handleCancel,
    handlePreview,
  };
}
